import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { dashboardApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Car, 
  Users, 
  TrendingUp, 
  IndianRupee,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  CheckCircle2,
  Wrench
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, variant = 'default' }) => (
  <Card className={`
    ${variant === 'premium' 
      ? 'bg-zinc-900 border-zinc-800 text-white' 
      : 'bg-zinc-900/50 border-zinc-800 text-white'
    }
    hover:border-zinc-700 transition-colors duration-200
  `}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-zinc-400">{title}</p>
          <p className="text-2xl lg:text-3xl font-bold text-white mt-2 tabular-nums">
            {value}
          </p>
          {subtitle && (
            <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${
              trend === 'up' ? 'text-emerald-400' : 'text-red-400'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center
          ${variant === 'premium' ? 'bg-[#D4AF37] text-black' : 'bg-zinc-800 text-[#D4AF37]'}
        `}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AlertItem = ({ alert }) => {
  const priorityColors = {
    high: 'bg-red-500/10 border-red-500/30 text-red-400',
    medium: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
    low: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  };

  return (
    <div className={`
      p-4 rounded-lg border ${priorityColors[alert.priority]}
      hover:bg-zinc-800/30 transition-colors duration-200
    `}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-white text-sm">{alert.entity_name}</p>
          <p className="text-sm text-zinc-400 mt-0.5">{alert.message}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs bg-transparent">
              {alert.days_remaining} days left
            </Badge>
            <Badge variant="outline" className={`text-xs bg-transparent capitalize ${
              alert.priority === 'high' ? 'border-red-500/50 text-red-400' :
              alert.priority === 'medium' ? 'border-yellow-500/50 text-yellow-400' :
              'border-blue-500/50 text-blue-400'
            }`}>
              {alert.priority}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, alertsRes] = await Promise.all([
          dashboardApi.getStats(),
          dashboardApi.getAlerts(),
        ]);
        setStats(statsRes.data);
        setAlerts(alertsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Layout title="Dashboard" subtitle="Loading...">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-6">
                <div className="h-24 animate-pulse bg-zinc-800 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </Layout>
    );
  }

  const fleet = stats?.fleet_overview || {};
  const financial = stats?.financial_summary || {};
  const profitMetrics = stats?.profit_metrics || {};

  return (
    <Layout 
      title="CEO Dashboard" 
      subtitle="Complete fleet overview and analytics"
    >
      <div className="space-y-8" data-testid="ceo-dashboard">
        {/* Key Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Fleet Value"
            value={formatCurrency(financial.total_fleet_value)}
            subtitle={`${fleet.total_vehicles || 0} vehicles`}
            icon={Car}
            variant="premium"
          />
          <StatCard
            title="Total Revenue"
            value={formatCurrency(financial.total_revenue)}
            subtitle="This period"
            icon={IndianRupee}
            trend="up"
            trendValue="+12.5%"
          />
          <StatCard
            title="YTD Operating Profit"
            value={formatCurrency(profitMetrics.ytd_operating_profit)}
            subtitle="Year to date"
            icon={TrendingUp}
            trend={profitMetrics.ytd_operating_profit >= 0 ? 'up' : 'down'}
            trendValue={profitMetrics.ytd_operating_profit >= 0 ? 'Profitable' : 'Loss'}
            variant="premium"
          />
          <StatCard
            title="MTD Operating Profit"
            value={formatCurrency(profitMetrics.mtd_operating_profit)}
            subtitle="Month to date"
            icon={Calendar}
            trend={profitMetrics.mtd_operating_profit >= 0 ? 'up' : 'down'}
            trendValue={profitMetrics.mtd_operating_profit >= 0 ? 'Profitable' : 'Loss'}
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Drivers"
            value={fleet.total_drivers || 0}
            subtitle="Active drivers"
            icon={Users}
          />
          <StatCard
            title="Total CAPEX"
            value={formatCurrency(financial.total_capex)}
            subtitle="Capital expenditure"
            icon={IndianRupee}
          />
          <StatCard
            title="Total OPEX"
            value={formatCurrency(financial.total_opex)}
            subtitle="Operational expenses"
            icon={Wrench}
          />
          <StatCard
            title="ROI"
            value={`${financial.roi || 0}%`}
            subtitle="Return on Investment"
            icon={TrendingUp}
            trend={financial.roi >= 0 ? 'up' : 'down'}
            trendValue={financial.roi >= 0 ? 'Positive' : 'Negative'}
          />
        </div>

        {/* Financial Overview & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Financial Summary Card */}
          <Card className="lg:col-span-2 bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-4">
              <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <IndianRupee className="w-5 h-5 text-[#D4AF37]" />
                Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* CAPEX vs OPEX */}
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 bg-zinc-800/50 rounded-xl">
                  <p className="text-sm text-zinc-400 mb-1">Total CAPEX</p>
                  <p className="text-2xl font-bold text-[#D4AF37] tabular-nums">
                    {formatCurrency(financial.total_capex)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">Capital expenditure</p>
                </div>
                <div className="p-4 bg-zinc-800/50 rounded-xl">
                  <p className="text-sm text-zinc-400 mb-1">Total OPEX</p>
                  <p className="text-2xl font-bold text-white tabular-nums">
                    {formatCurrency(financial.total_opex)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">Operational expenses</p>
                </div>
              </div>

              {/* Profitability Metrics */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-zinc-400">Profitability Metrics</h4>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-400">Gross Revenue</span>
                      <span className="text-white tabular-nums">{formatCurrency(financial.total_revenue)}</span>
                    </div>
                    <Progress value={100} className="h-2 bg-zinc-800" />
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-400">Operating Expenses</span>
                      <span className="text-white tabular-nums">{formatCurrency(financial.total_opex)}</span>
                    </div>
                    <Progress 
                      value={financial.total_revenue > 0 ? (financial.total_opex / financial.total_revenue) * 100 : 0} 
                      className="h-2 bg-zinc-800"
                    />
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-zinc-400">Net Profit</span>
                      <span className={`tabular-nums ${financial.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(financial.net_profit)}
                      </span>
                    </div>
                    <Progress 
                      value={financial.total_revenue > 0 ? Math.abs(financial.net_profit / financial.total_revenue) * 100 : 0} 
                      className={`h-2 bg-zinc-800 ${financial.net_profit >= 0 ? '[&>div]:bg-emerald-500' : '[&>div]:bg-red-500'}`}
                    />
                  </div>
                </div>
              </div>

              {/* Operating Profit Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-zinc-800">
                <div className="text-center">
                  <p className="text-2xl font-bold text-[#D4AF37] tabular-nums">{formatCurrency(profitMetrics.ytd_operating_profit)}</p>
                  <p className="text-xs text-zinc-500 mt-1">YTD Profit</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white tabular-nums">{formatCurrency(profitMetrics.mtd_operating_profit)}</p>
                  <p className="text-xs text-zinc-500 mt-1">MTD Profit</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white tabular-nums">{fleet.total_vehicles || 0}</p>
                  <p className="text-xs text-zinc-500 mt-1">Active Vehicles</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alerts Card */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <AlertTriangle className="w-5 h-5 text-[#D4AF37]" />
                  Active Alerts
                </CardTitle>
                <Link 
                  to="/alerts" 
                  className="text-sm text-[#D4AF37] hover:underline"
                  data-testid="view-all-alerts-link"
                >
                  View All
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {alerts.length > 0 ? (
                alerts.map((alert, index) => (
                  <AlertItem key={alert.id || index} alert={alert} />
                ))
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-zinc-400">No active alerts</p>
                  <p className="text-sm text-zinc-500">All documents are up to date</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Link 
            to="/vehicles" 
            className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-[#D4AF37]/50 transition-colors group"
            data-testid="quick-action-vehicles"
          >
            <Car className="w-8 h-8 text-[#D4AF37] mb-3 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-white">Manage Vehicles</p>
            <p className="text-sm text-zinc-500">{fleet.total_vehicles || 0} vehicles</p>
          </Link>
          <Link 
            to="/drivers" 
            className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-[#D4AF37]/50 transition-colors group"
            data-testid="quick-action-drivers"
          >
            <Users className="w-8 h-8 text-[#D4AF37] mb-3 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-white">Manage Drivers</p>
            <p className="text-sm text-zinc-500">{fleet.total_drivers || 0} drivers</p>
          </Link>
          <Link 
            to="/expenses" 
            className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-[#D4AF37]/50 transition-colors group"
            data-testid="quick-action-expenses"
          >
            <Wrench className="w-8 h-8 text-[#D4AF37] mb-3 group-hover:scale-110 transition-transform" />
            <p className="font-medium text-white">Add Expense</p>
            <p className="text-sm text-zinc-500">CAPEX & OPEX</p>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
